<template>
  <span :class="mergedClass" v-bind="restAttrs">
    <span
      v-if="dot"
      aria-hidden="true"
      :class="
        cn(
          'tw:relative tw:inline-block tw:size-1.5 tw:shrink-0 tw:rounded-full tw:bg-current',
          pulse && [
            'tw:before:absolute tw:before:inset-0 tw:before:rounded-full',
            'tw:before:bg-[color-mix(in_srgb,currentColor_60%,#fff)]',
            'tw:before:opacity-55 tw:before:content-[\'\']',
            'tw:before:animate-pulse-dot tw:motion-reduce:before:animate-none',
          ]
        )
      " />

    <component v-if="icon" :is="icon" :size="12" :stroke-width="2" class="tw:shrink-0" />

    <slot />

    <button
      v-if="hasRemove"
      type="button"
      :aria-label="removeLabel ?? 'Entfernen'"
      :class="REMOVE"
      @pointerdown.stop
      @click.stop="emit('remove', $event)">
      <IconX :size="12" :stroke-width="2" />
    </button>
  </span>
</template>

<script setup lang="ts">
// LeadPill — mirrors the LEAD Design System <Pill> component 1:1.
// Class strings and structure extracted from
// .claude/skills/migrate-tailwind/design-source/components/Pill/Pill.tsx.
//
// GEOMETRY: 22px tall. Figma reports the frame as 22px with the text inset
// 10px horizontally and 2px vertically, but strokes sit INSIDE the frame — so
// with border-box the 1px border comes OUT of the padding: 1 + 9 = 10 and
// 1 + 1 + 18 + 1 + 1 = 22. Writing `py-0.5 px-2.5` plus a border renders 24px.
import type { Component } from 'vue';
import { computed, useAttrs } from 'vue';
import { IconX } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';

// Opt out of automatic $attrs.class merging so consumer classes flow through
// cn() and beat DS defaults deterministically.
defineOptions({ inheritAttrs: false });

export type LeadPillTone = 'neutral' | 'primary' | 'success' | 'danger' | 'warning';

export interface LeadPillProps {
  tone?: LeadPillTone;
  /** Leading status dot, tinted to match the tone. */
  dot?: boolean;
  /** Breathe the dot. Use only when the state is genuinely live — running, syncing. */
  pulse?: boolean;
  /** Leading icon component. Mutually sensible with `dot`, but pick one. */
  icon?: Component;
  /** When true, renders a trailing ✕ that emits `remove`. */
  removable?: boolean;
  /** Accessible name for the ✕. Falls back to `Entfernen`. */
  removeLabel?: string;
}

const props = withDefaults(defineProps<LeadPillProps>(), {
  tone: 'neutral',
  dot: false,
  pulse: false,
  icon: undefined,
  removable: false,
  removeLabel: undefined,
});

const emit = defineEmits<{
  remove: [event: MouseEvent];
}>();

// The neutral tone's two greys come from the Badges Figma node, whose scale is
// NOT the one the rest of this system uses — they are role-named tokens
// (`pill-border`, `pill-fg`) precisely so they cannot be "tidied" into the
// main gray ramp.
const TONE: Record<LeadPillTone, string> = {
  neutral: 'tw:bg-white tw:border-pill-border tw:text-pill-fg',
  primary: 'tw:bg-blue-50 tw:border-blue-200 tw:text-blue-500',
  success: 'tw:bg-success-50 tw:border-success-200 tw:text-success-700',
  danger: 'tw:bg-red-50 tw:border-red-200 tw:text-red-600',
  warning: 'tw:bg-orange-50 tw:border-orange-200 tw:text-orange-600',
};

// The ✕. A real <button>, so it's tabbable and answers Enter/Space. Opacity
// (not a second colour) keeps it inside the tone — five tones would otherwise
// need five hover greys.
const REMOVE = [
  'tw:-mr-[3px] tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:p-px',
  'tw:cursor-pointer tw:opacity-60 tw:transition-opacity tw:duration-150 tw:ease-in-out tw:hover:opacity-100',
  'tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)] tw:focus-visible:opacity-100',
  // beat Tabler's `button { border-radius: 0 }` reset
  'tw:rounded-full! tw:border-0 tw:bg-transparent',
].join(' ');

const BASE =
  'tw:inline-flex tw:items-center tw:justify-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:border tw:border-solid ' +
  'tw:px-[9px] tw:py-px tw:text-12 tw:font-medium';

/* --------------------------------- COMPUTED ------------------------------- */

const hasRemove = computed(() => props.removable);

const attrs = useAttrs();

const mergedClass = computed(() => cn(BASE, TONE[props.tone], attrs.class as string | undefined));

const restAttrs = computed(() => {
  const { class: _c, ...rest } = attrs;
  return rest;
});
</script>
